// 근무 이력 조회 페이지
import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stack,
  Heading,
  useToast,
  Button,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { getUserAssignments, getJobPosting } from 'utils/firestoreUtils';
import { useAuth } from 'contexts/AuthContext';

function WorkHistory() {
  const [assignments, setAssignments] = useState([]);
  const [assignmentsWithJobs, setAssignmentsWithJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState('all');
  const [stats, setStats] = useState({
    totalWorks: 0,
    totalHours: 0,
    totalEarnings: 0,
    avgRating: 0
  });

  const { currentUser } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (currentUser) {
      loadWorkHistory();
    }
  }, [currentUser]);

  useEffect(() => {
    calculateStats();
  }, [assignmentsWithJobs, filterYear]);

  async function loadWorkHistory() {
    try {
      setLoading(true);
      const data = await getUserAssignments(currentUser.uid);

      // 각 근무에 대한 공고 정보 가져오기
      const withJobs = await Promise.all(
        data.map(async (assignment) => {
          const job = await getJobPosting(assignment.jobId);
          return { ...assignment, job };
        })
      );

      setAssignments(data);
      setAssignmentsWithJobs(withJobs);
    } catch (error) {
      console.error('이력 로딩 오류:', error);
      toast({
        title: '오류',
        description: '근무 이력을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }

  function calculateStats() {
    let filtered = assignmentsWithJobs;

    if (filterYear !== 'all') {
      filtered = assignmentsWithJobs.filter(a => {
        const year = new Date(a.confirmedAt?.toDate()).getFullYear();
        return year.toString() === filterYear;
      });
    }

    const totalWorks = filtered.length;
    const totalEarnings = filtered.reduce((sum, a) => sum + (a.actualWage || 0), 0);
    const ratings = filtered.filter(a => a.rating).map(a => a.rating);
    const avgRating = ratings.length > 0
      ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length
      : 0;

    setStats({
      totalWorks,
      totalHours: 0, // 계산 로직 필요
      totalEarnings,
      avgRating
    });
  }

  function getStatusColor(status) {
    switch (status) {
      case 'selected': return 'blue';
      case 'completed': return 'green';
      case 'canceled': return 'red';
      default: return 'gray';
    }
  }

  function getStatusText(status) {
    switch (status) {
      case 'selected': return '선정';
      case 'completed': return '완료';
      case 'canceled': return '취소';
      default: return status;
    }
  }

  // 연도 목록 생성
  const years = [...new Set(
    assignmentsWithJobs.map(a => 
      new Date(a.confirmedAt?.toDate()).getFullYear()
    )
  )].sort((a, b) => b - a);

  const filteredAssignments = filterYear === 'all'
    ? assignmentsWithJobs
    : assignmentsWithJobs.filter(a => {
        const year = new Date(a.confirmedAt?.toDate()).getFullYear();
        return year.toString() === filterYear;
      });

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 통계 카드 */}
      <Grid
        templateColumns={{ sm: '1fr', md: 'repeat(4, 1fr)' }}
        gap={6}
        mb={6}
      >
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>총 근무 횟수</StatLabel>
              <StatNumber color="blue.500">{stats.totalWorks}</StatNumber>
              <StatHelpText>회</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>총 수입</StatLabel>
              <StatNumber color="green.500">
                {stats.totalEarnings.toLocaleString()}
              </StatNumber>
              <StatHelpText>원</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>평균 평점</StatLabel>
              <StatNumber color="purple.500">
                {stats.avgRating.toFixed(1)}
              </StatNumber>
              <StatHelpText>/ 5.0</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>완료된 근무</StatLabel>
              <StatNumber color="teal.500">
                {filteredAssignments.filter(a => a.status === 'completed').length}
              </StatNumber>
              <StatHelpText>회</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </Grid>

      {/* 이력 목록 */}
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }}>
        <CardHeader p="6px 0px 22px 0px">
          <Flex justify="space-between" align="center">
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              근무 이력
            </Text>
            <Select
              w="200px"
              value={filterYear}
              ml={4}
              onChange={(e) => setFilterYear(e.target.value)}
            >
              <option value="all">전체 연도</option>
              {years.map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </Select>
          </Flex>
        </CardHeader>

        <CardBody>
          {loading ? (
            <Text textAlign="center" py={8}>로딩 중...</Text>
          ) : filteredAssignments.length === 0 ? (
            <Box textAlign="center" py={12}>
              <Text fontSize="lg" color="gray.500" mb={4}>
                근무 이력이 없습니다
              </Text>
              <Text fontSize="sm" color="gray.400">
                공고에 지원하고 선정되면 여기에 표시됩니다
              </Text>
            </Box>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" color={textColor}>
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor}>공고명</Th>
                    <Th borderColor={borderColor}>근무 위치</Th>
                    <Th borderColor={borderColor}>근무일</Th>
                    <Th borderColor={borderColor}>근무 시간</Th>
                    <Th borderColor={borderColor}>급여</Th>
                    <Th borderColor={borderColor}>평점</Th>
                    <Th borderColor={borderColor}>상태</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredAssignments.map((assignment) => (
                    <Tr key={assignment.id}>
                      <Td borderColor={borderColor}>
                        <Text fontWeight="medium">
                          {assignment.job?.title || '삭제된 공고'}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        {assignment.job?.location || '-'}
                      </Td>
                      <Td borderColor={borderColor}>
                        {assignment.workDates?.length > 0
                          ? assignment.workDates.length > 1
                            ? `${assignment.workDates[0]} 외 ${assignment.workDates.length - 1}일`
                            : assignment.workDates[0]
                          : '-'
                        }
                      </Td>
                      <Td borderColor={borderColor}>
                        {assignment.actualWorkHours ? (
                          <Text fontWeight="medium">
                            {assignment.actualWorkHours}시간
                          </Text>
                        ) : (
                          <Text color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text fontWeight="bold">
                          {assignment.actualWage
                            ? `${assignment.actualWage.toLocaleString()}원`
                            : '-'
                          }
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        {assignment.rating ? (
                          <Flex align="center" gap={1}>
                            <Text fontWeight="bold" color="yellow.500">
                              {assignment.rating.toFixed(1)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">/ 5.0</Text>
                          </Flex>
                        ) : (
                          <Text color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Badge colorScheme={getStatusColor(assignment.status)}>
                          {getStatusText(assignment.status)}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {/* 평가 코멘트 */}
          {filteredAssignments.some(a => a.evaluationNote) && (
            <Box mt={8}>
              <Heading size="md" mb={4}>평가 코멘트</Heading>
              <Stack spacing={4}>
                {filteredAssignments
                  .filter(a => a.evaluationNote)
                  .map((assignment) => (
                    <Card key={assignment.id} bg="gray.50" _dark={{ bg: 'gray.700' }}>
                      <CardBody>
                        <Flex justify="space-between" mb={2}>
                          <Text fontWeight="bold">
                            {assignment.job?.title}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {assignment.confirmedAt?.toDate?.()?.toLocaleDateString()}
                          </Text>
                        </Flex>
                        <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                          {assignment.evaluationNote}
                        </Text>
                      </CardBody>
                    </Card>
                  ))}
              </Stack>
            </Box>
          )}
        </CardBody>
      </Card>
    </Flex>
  );
}

export default WorkHistory;

